import React, { useRef, useState } from 'react';
import { dateCreatedOptions, leadSourceOptions, leadStatusOptions, languageOptions } from '../../Constant';
import Form from '../../molecules/Form/Form';
import Field from '../Field';
import { IoIosArrowDown } from 'react-icons/io';
import { IoCloseOutline } from 'react-icons/io5';
import dummyImg from '../../../assets/avatar-2.svg';
import iconList from '../../../assets/icon-list.svg';
import iconCity from '../../../assets/icon-city.svg';
import iconFlag from '../../../assets/icon-flag.svg';
import { LocationFilterTags, StyledFiltersList } from './LeadsDetails.styles';
import { flattenFully, handleDebouncedQueryUpdate } from '../../../helpers/common';

let cachedAllLanguagesOptions = null;

const getAllLanguagesOptions = () => {
    if (cachedAllLanguagesOptions) return cachedAllLanguagesOptions;

    try {
        // Modern runtimes (Chromium/Node with full ICU) can provide a comprehensive list.
        if (typeof Intl !== 'undefined' && typeof Intl.supportedValuesOf === 'function') {
            const codes = Intl.supportedValuesOf('language') || [];
            const displayNames = new Intl.DisplayNames(['en'], { type: 'language' });
            const options = codes
                .map(code => {
                    const label = displayNames.of(code);
                    return label ? { value: code, label } : null;
                })
                .filter(Boolean)
                .sort((a, b) => a.label.localeCompare(b.label));

            cachedAllLanguagesOptions = options;
            return options;
        }
    } catch (e) {
        // Fallback below
    }

    cachedAllLanguagesOptions = Array.isArray(languageOptions) ? languageOptions : [];
    return cachedAllLanguagesOptions;
};

const parseTagList = (arrayVal, stringVal) => {
    if (Array.isArray(arrayVal) && arrayVal.length > 0) return arrayVal;
    if (!stringVal) return [];
    return String(stringVal)
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
};

const Filters = ({
    searchQuery,
    setSearchQuery,
    campaigns,
    tags,
    lists,
    filters: customFilters,
    industryOption: customIndustryOptions,
    location: customLocationOptions,
    leadOwners: customLeadOwners,
    hideListFilter = false
}) => {
    const [openFilters, setOpenFilters] = useState([]);
    const [locationDraft, setLocationDraft] = useState('');
    const [companyDraft, setCompanyDraft] = useState('');
    const [industryDraft, setIndustryDraft] = useState('');
    const [headlineDraft, setHeadlineDraft] = useState('');
    const debounceRef = useRef(null);
    const [textDrafts, setTextDrafts] = useState({});

    const getTextDraft = queryKey =>
        textDrafts[queryKey] ?? flattenFully(searchQuery[queryKey], true) ?? '';

    const handleTextFilterChange = (queryKey, rawValue) => {
        const value = rawValue ?? '';
        setTextDrafts(prev => ({ ...prev, [queryKey]: value }));
        handleDebouncedQueryUpdate(debounceRef, setSearchQuery, {
            [queryKey]: flattenFully(value, true) || '',
        });
    };

    const toggleFilter = filterName => {
        setOpenFilters(prev => (prev.includes(filterName) ? prev.filter(f => f !== filterName) : [...prev, filterName]));
    };

    const getLocationTags = () => {
        if (Array.isArray(searchQuery.locations) && searchQuery.locations.length > 0) {
            return searchQuery.locations;
        }
        if (!searchQuery.location) return [];
        return String(searchQuery.location)
            .split(',')
            .map(s => s.trim())
            .filter(Boolean);
    };

    const addLocationTag = raw => {
        const tag = String(raw || '').trim();
        if (!tag) return;
        const current = getLocationTags();
        if (current.some(l => l.toLowerCase() === tag.toLowerCase())) {
            setLocationDraft('');
            return;
        }
        const next = [...current, tag];
        setSearchQuery(prev => ({
            ...prev,
            locations: next,
            location: next.join(','),
            page: 1,
            lastSeenId: null,
        }));
        setLocationDraft('');
    };

    const removeLocationTag = tag => {
        const next = getLocationTags().filter(l => l !== tag);
        setSearchQuery(prev => ({
            ...prev,
            locations: next,
            location: next.length ? next.join(',') : '',
            page: 1,
            lastSeenId: null,
        }));
    };

    const getCompanyTags = () =>
        parseTagList(searchQuery.companyNames, searchQuery.companyName || searchQuery.company);
    const addCompanyTag = raw => {
        const tag = String(raw || '').trim();
        if (!tag) return;
        const current = getCompanyTags();
        if (current.some(t => t.toLowerCase() === tag.toLowerCase())) {
            setCompanyDraft('');
            return;
        }
        const next = [...current, tag];
        const joined = next.join(',');
        setSearchQuery(prev => ({
            ...prev,
            companyNames: next,
            companyName: joined,
            company: joined,
            page: 1,
            lastSeenId: null,
        }));
        setCompanyDraft('');
    };
    const removeCompanyTag = tag => {
        const next = getCompanyTags().filter(t => t !== tag);
        const joined = next.length ? next.join(',') : '';
        setSearchQuery(prev => ({
            ...prev,
            companyNames: next,
            companyName: joined,
            company: joined,
            page: 1,
            lastSeenId: null,
        }));
    };

    const getIndustryTags = () => parseTagList(searchQuery.industries, searchQuery.industry);
    const addIndustryTag = raw => {
        const tag = String(raw || '').trim();
        if (!tag) return;
        const current = getIndustryTags();
        if (current.some(t => t.toLowerCase() === tag.toLowerCase())) {
            setIndustryDraft('');
            return;
        }
        const next = [...current, tag];
        setSearchQuery(prev => ({
            ...prev,
            industries: next,
            industry: next.join(','),
            page: 1,
            lastSeenId: null,
        }));
        setIndustryDraft('');
    };
    const removeIndustryTag = tag => {
        const next = getIndustryTags().filter(t => t !== tag);
        setSearchQuery(prev => ({
            ...prev,
            industries: next,
            industry: next.length ? next.join(',') : '',
            page: 1,
            lastSeenId: null,
        }));
    };

    const getHeadlineTags = () => parseTagList(searchQuery.headlines, searchQuery.headline);
    const addHeadlineTag = raw => {
        const tag = String(raw || '').trim();
        if (!tag) return;
        const current = getHeadlineTags();
        if (current.some(t => t.toLowerCase() === tag.toLowerCase())) {
            setHeadlineDraft('');
            return;
        }
        const next = [...current, tag];
        setSearchQuery(prev => ({
            ...prev,
            headlines: next,
            headline: next.join(','),
            page: 1,
            lastSeenId: null,
        }));
        setHeadlineDraft('');
    };
    const removeHeadlineTag = tag => {
        const next = getHeadlineTags().filter(t => t !== tag);
        setSearchQuery(prev => ({
            ...prev,
            headlines: next,
            headline: next.length ? next.join(',') : '',
            page: 1,
            lastSeenId: null,
        }));
    };

    const renderTextTagFilter = ({ placeholder, draft, setDraft, tags, onAdd, onRemove }) => (
        <div className="list">
            <Form.Item
                colorBg
                inputSm
                noMargin
                label=""
                type="text"
                autoComplete="one-time-code"
                placeholder={placeholder}
                value={draft}
                onChange={e => setDraft(e?.target?.value || '')}
                onKeyDown={e => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        onAdd(draft);
                    }
                }}>
                <Field />
            </Form.Item>
            {tags.length > 0 ? (
                <LocationFilterTags>
                    {tags.map(tag => (
                        <span key={tag} className="location-tag">
                            {tag}
                            <button type="button" aria-label={`Remove ${tag}`} onClick={() => onRemove(tag)}>
                                <IoCloseOutline />
                            </button>
                        </span>
                    ))}
                </LocationFilterTags>
            ) : null}
        </div>
    );

    const leadOwners = customLeadOwners || [
        { id: 1, name: 'Alex Johnson', avatar: dummyImg },
        { id: 2, name: 'Sarah Williams', avatar: dummyImg },
        { id: 3, name: 'Michael Brown', avatar: dummyImg },
    ];
    const industryOptions = customIndustryOptions || [
        { id: 1, name: 'Real Estate ', avatar: iconList },
        { id: 1, name: 'B2B SaaS ', avatar: iconList },
    ];
    const locationOptions = customLocationOptions || [
        { id: 1, name: 'California', avatar: iconCity },
        { id: 2, name: 'Florida', avatar: iconCity },
        { id: 3, name: 'Mexico', avatar: iconFlag },
    ];
    const emailStatusOptions = [
        { value: 'Verified', label: 'Verified' },
        { value: 'Extrapolated', label: 'Extrapolated' },
        // { value: 'Unverifiable', label: 'Unverifiable' },
        // { value: 'Unknown', label: 'Unknown' },
        // { value: 'Invalid', label: 'Invalid' },
    ];

    const textFiltersMap = {
        'Email': 'email',
        'Function': 'person_function',
        'Phone': 'phone',
        'LinkedIn URL': 'person_linkedin_url',
        'City': 'person_city',
        'Country': 'person_country',
        'Location': 'location',
        'Organization ID': 'current_organization_id',
        'LinkedIn Connections': 'person_linkedin_connections',
        'Job Title': 'jobTitle',
        'Title': 'title',
        'State': 'person_state',
        'Industry': 'industry',
        'Headline': 'headline',
    };
    const renderFilterContent = filter => {
        switch (filter) {
            case 'Date Created':
                return (
                    <div className="list">
                        <Form.Item
                            colorBg
                            selectSm
                            noMargin
                            label=""
                            type="select"
                            rounded
                            isClearable
                            name="dateCreatedSelect"
                            autoComplete="one-time-code"
                            placeholder="Select Date Range"
                            options={dateCreatedOptions}
                            value={
                                searchQuery.dateCreatedFilter
                                    ? dateCreatedOptions.find(opt => opt.value === searchQuery.dateCreatedFilter)
                                    : null
                            }
                            onChange={e => {
                                const value = e?.target?.value?.value || e?.target?.value || e?.value || e;
                                const finalValue = (value && typeof value === 'object' && 'target' in value) ? '' : (value?.value || value);
                                setSearchQuery(prev => ({
                                    ...prev,
                                    dateCreatedFilter: finalValue || '',
                                    page: 1,
                                    lastSeenId: null,
                                }));
                            }}>
                            <Field />
                        </Form.Item>
                    </div>
                );

            case 'Lead Status':
                return (
                    <div className="list">
                        <div className="checkbox-list">
                            {leadStatusOptions.map(option => {
                                const isChecked = (searchQuery.status || []).includes(option.value);
                                return (
                                    <Field
                                        key={option.value}
                                        type="checkbox"
                                        squareCheck
                                        noMargin
                                        name={`leadStatus-${option.value}`}
                                        label={option.label}
                                        value={isChecked}
                                        onChange={({ target: { value } }) => {
                                            setSearchQuery(prev => {
                                                const current = prev.status || [];
                                                const exists = current.includes(option.value);
                                                const next = value
                                                    ? exists
                                                        ? current
                                                        : [...current, option.value]
                                                    : current.filter(v => v !== option.value);
                                                return {
                                                    ...prev,
                                                    status: next,
                                                    page: 1,
                                                    lastSeenId: null,
                                                };
                                            });
                                        }}
                                    />
                                );
                            })}
                        </div>
                    </div>
                );

            case 'Lead Owner':
                return (
                    <div className="list">
                        <Form.Item
                            colorBg
                            inputSm
                            noMargin
                            label=""
                            type="search"
                            rounded
                            name="search"
                            autoComplete="one-time-code"
                            placeholder="Search for user...">
                            <Field />
                        </Form.Item>
                        <ul className="item-list">
                            {leadOwners.map(user => (
                                <li key={user.id}>
                                    <div className="item">
                                        <div className="img-wrap">
                                            <img src={user.avatar} alt={user.name} className="avatar" />
                                        </div>
                                        <span className="name">{user.name}</span>
                                        <IoCloseOutline className="btn-close" />
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                );

            case 'Contacted Status': {
                const contactedStatusOptions = [
                    { value: 'ALL', label: 'All' },
                    { value: 'CONTACTED', label: 'Contacted' },
                    { value: 'UNCONTACTED_ONLY', label: 'Uncontacted Only' },
                ];

                return (
                    <ul className="list">
                        {contactedStatusOptions.map(option => (
                            <li key={option.value}>
                                <label className="custom-radio">
                                    <input
                                        type="radio"
                                        name="contactedStatus"
                                        checked={searchQuery.contactedStatus === option.value}
                                        onChange={() =>
                                            setSearchQuery(prev => ({
                                                ...prev,
                                                contactedStatus: option.value,
                                                page: 1,
                                                lastSeenId: null,
                                            }))
                                        }
                                    />
                                    <span className="text">{option.label}</span>
                                </label>
                            </li>
                        ))}
                    </ul>
                );
            }

            case 'Email': {
                const emailExistsOptions = [
                    { value: 'ALL', label: 'All' },
                    { value: 'EXISTS', label: 'Exist' },
                    { value: 'NOT_EXISTS', label: 'Not Exist' },
                ];

                return (
                    <ul className="list">
                        {emailExistsOptions.map(option => (
                            <li key={option.value}>
                                <label className="custom-radio">
                                    <input
                                        type="radio"
                                        name="emailExists"
                                        checked={(searchQuery.emailExists || 'ALL') === option.value}
                                        onChange={() =>
                                            setSearchQuery(prev => ({
                                                ...prev,
                                                emailExists: option.value,
                                                page: 1,
                                                lastSeenId: null,
                                            }))
                                        }
                                    />
                                    <span className="text">{option.label}</span>
                                </label>
                            </li>
                        ))}
                    </ul>
                );
            }

            case 'LinkedIn Profile': {
                const linkedinExistsOptions = [
                    { value: 'ALL', label: 'All' },
                    { value: 'EXISTS', label: 'Exist' },
                    { value: 'NOT_EXISTS', label: 'Not Exist' },
                ];

                return (
                    <ul className="list">
                        {linkedinExistsOptions.map(option => (
                            <li key={option.value}>
                                <label className="custom-radio">
                                    <input
                                        type="radio"
                                        name="linkedinProfileExists"
                                        checked={(searchQuery.linkedinProfileExists || 'ALL') === option.value}
                                        onChange={() =>
                                            setSearchQuery(prev => ({
                                                ...prev,
                                                linkedinProfileExists: option.value,
                                                page: 1,
                                                lastSeenId: null,
                                            }))
                                        }
                                    />
                                    <span className="text">{option.label}</span>
                                </label>
                            </li>
                        ))}
                    </ul>
                );
            }

            case 'Language': {
                const languageTags = parseTagList(searchQuery.languages, searchQuery.language);
                const languageOptions = getAllLanguagesOptions();
                const selectedLanguages = languageOptions.filter(
                    opt => languageTags.includes(opt.label) || languageTags.includes(opt.value),
                );
                return (
                    <div className="list">
                        <Form.Item
                            colorBg
                            selectSm
                            noMargin
                            label=""
                            type="select"
                            rounded
                            isMulti
                            name="languageSelect"
                            placeholder="Select languages"
                            options={languageOptions}
                            value={selectedLanguages}
                            onChange={e => {
                                const value = e?.target?.value || e;
                                const selectedArray = Array.isArray(value) ? value : value ? [value] : [];
                                const labels = selectedArray
                                    .map(item => String(item?.label ?? item?.value ?? item).trim())
                                    .filter(Boolean);
                                setSearchQuery(prev => ({
                                    ...prev,
                                    languages: labels,
                                    language: labels.join(','),
                                    page: 1,
                                    lastSeenId: null,
                                }));
                            }}>
                            <Field />
                        </Form.Item>
                    </div>
                );
            }

            case 'Open to Work': {
                const openToWorkOptions = [
                    { value: 'ALL', label: 'All' },
                    { value: 'YES', label: 'Yes' },
                    { value: 'NO', label: 'No' },
                ];

                return (
                    <ul className="list">
                        {openToWorkOptions.map(option => (
                            <li key={option.value}>
                                <label className="custom-radio">
                                    <input
                                        type="radio"
                                        name="openToWork"
                                        checked={(searchQuery.openToWork || 'ALL') === option.value}
                                        onChange={() =>
                                            setSearchQuery(prev => ({
                                                ...prev,
                                                openToWork: option.value,
                                                page: 1,
                                                lastSeenId: null,
                                            }))
                                        }
                                    />
                                    <span className="text">{option.label}</span>
                                </label>
                            </li>
                        ))}
                    </ul>
                );
            }

            case 'Is Hiring': {
                const isHiringOptions = [
                    { value: 'ALL', label: 'All' },
                    { value: 'YES', label: 'Yes' },
                    { value: 'NO', label: 'No' },
                ];

                return (
                    <ul className="list">
                        {isHiringOptions.map(option => (
                            <li key={option.value}>
                                <label className="custom-radio">
                                    <input
                                        type="radio"
                                        name="isHiring"
                                        checked={(searchQuery.isHiring || 'ALL') === option.value}
                                        onChange={() =>
                                            setSearchQuery(prev => ({
                                                ...prev,
                                                isHiring: option.value,
                                                page: 1,
                                                lastSeenId: null,
                                            }))
                                        }
                                    />
                                    <span className="text">{option.label}</span>
                                </label>
                            </li>
                        ))}
                    </ul>
                );
            }

            case 'Lead Source':
                return (
                    <div className="list">
                        <Form.Item
                            colorBg
                            selectSm
                            noMargin
                            label=""
                            type="select"
                            rounded
                            isMulti
                            name="leadSourceSelect"
                            placeholder="Select Lead Source"
                            options={leadSourceOptions}
                            value={
                                Array.isArray(searchQuery.source)
                                    ? leadSourceOptions.filter(opt => searchQuery.source.includes(opt.value))
                                    : []
                            }
                            onChange={e => {
                                const value = e?.target?.value || e;
                                const selectedArray = Array.isArray(value) ? value : value ? [value] : [];
                                const normalized = selectedArray
                                    .map(item => item?.value || item)
                                    .filter(v => v !== undefined && v !== null);
                                setSearchQuery(prev => ({
                                    ...prev,
                                    source: normalized,
                                    page: 1,
                                    lastSeenId: null,
                                }));
                            }}>
                            <Field />
                        </Form.Item>
                    </div>
                );

            case 'Campaign':
                return (
                    <div className="list">
                        <Form.Item
                            colorBg
                            selectSm
                            noMargin
                            label=""
                            type="select"
                            rounded
                            isMulti
                            name="campaignSelect"
                            placeholder="Select Campaigns"
                            options={campaigns}
                            value={
                                Array.isArray(searchQuery.campaigns)
                                    ? campaigns.filter(opt => searchQuery.campaigns.includes(opt.value))
                                    : []
                            }
                            onChange={e => {
                                const value = e?.target?.value || e;
                                const selectedArray = Array.isArray(value) ? value : value ? [value] : [];
                                const normalized = selectedArray
                                    .map(item => item?.value || item)
                                    .filter(v => v !== undefined && v !== null);
                                setSearchQuery(prev => ({
                                    ...prev,
                                    campaigns: normalized,
                                    page: 1,
                                    lastSeenId: null,
                                }));
                            }}>
                            <Field />
                        </Form.Item>
                    </div>
                );

            case 'Tags':
                return (
                    <div className="list">
                        <Form.Item
                            colorBg
                            selectSm
                            noMargin
                            label=""
                            type="select"
                            rounded
                            isMulti
                            name="tagsSelect"
                            placeholder="Select Tags"
                            options={tags}
                            value={
                                Array.isArray(searchQuery.tagsFilter)
                                    ? tags.filter(opt => searchQuery.tagsFilter.includes(opt.value))
                                    : []
                            }
                            onChange={e => {
                                const value = e?.target?.value || e;
                                const selectedArray = Array.isArray(value) ? value : value ? [value] : [];
                                const normalized = selectedArray
                                    .map(item => item?.value || item)
                                    .filter(v => v !== undefined && v !== null);
                                setSearchQuery(prev => ({
                                    ...prev,
                                    tagsFilter: normalized,
                                    page: 1,
                                    lastSeenId: null,
                                }));
                            }}>
                            <Field />
                        </Form.Item>
                    </div>
                );

            case 'List':
                return (
                    <div className="list">
                        <Form.Item
                            colorBg
                            selectSm
                            noMargin
                            label=""
                            type="select"
                            rounded
                            isMulti
                            name="listsSelect"
                            placeholder="Select Lists"
                            options={lists}
                            value={
                                Array.isArray(searchQuery.listsFilter)
                                    ? lists.filter(opt => searchQuery.listsFilter.includes(opt.value))
                                    : []
                            }
                            onChange={e => {
                                const value = e?.target?.value || e;
                                const selectedArray = Array.isArray(value) ? value : value ? [value] : [];
                                const normalized = selectedArray
                                    .map(item => item?.value || item)
                                    .filter(v => v !== undefined && v !== null);
                                setSearchQuery(prev => ({
                                    ...prev,
                                    listsFilter: normalized,
                                    page: 1,
                                    lastSeenId: null,
                                }));
                            }}>
                            <Field />
                        </Form.Item>
                    </div>
                );

            case 'Company Name':
                return renderTextTagFilter({
                    placeholder: 'Enter company name',
                    draft: companyDraft,
                    setDraft: setCompanyDraft,
                    tags: getCompanyTags(),
                    onAdd: addCompanyTag,
                    onRemove: removeCompanyTag,
                });

            case 'Location': {
                const locationTags = getLocationTags();
                return (
                    <div className="list">
                        <Form.Item
                            colorBg
                            inputSm
                            noMargin
                            label=""
                            type="text"
                            name="locationInput"
                            autoComplete="one-time-code"
                            placeholder="Enter location"
                            value={locationDraft}
                            onChange={e => setLocationDraft(e?.target?.value || '')}
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    addLocationTag(locationDraft);
                                }
                            }}>
                            <Field />
                        </Form.Item>
                        {locationTags.length > 0 ? (
                            <LocationFilterTags>
                                {locationTags.map(loc => (
                                    <span key={loc} className="location-tag">
                                        {loc}
                                        <button
                                            type="button"
                                            aria-label={`Remove ${loc}`}
                                            onClick={() => removeLocationTag(loc)}>
                                            <IoCloseOutline />
                                        </button>
                                    </span>
                                ))}
                            </LocationFilterTags>
                        ) : null}
                    </div>
                );
            }

            case 'Industry':
                return renderTextTagFilter({
                    placeholder: 'Enter industry',
                    draft: industryDraft,
                    setDraft: setIndustryDraft,
                    tags: getIndustryTags(),
                    onAdd: addIndustryTag,
                    onRemove: removeIndustryTag,
                });

            case 'Headline':
                return renderTextTagFilter({
                    placeholder: 'Enter headline',
                    draft: headlineDraft,
                    setDraft: setHeadlineDraft,
                    tags: getHeadlineTags(),
                    onAdd: addHeadlineTag,
                    onRemove: removeHeadlineTag,
                });

            case 'Job Title':
                return (
                    <div className="list">
                        <Form.Item
                            colorBg
                            inputSm
                            noMargin
                            label=""
                            type="text"
                            rounded
                            name="jobTitle"
                            placeholder="Enter job title"
                            value={getTextDraft('jobTitle')}
                            onChange={e => handleTextFilterChange('jobTitle', e?.target?.value || '')}>
                            <Field />
                        </Form.Item>
                    </div>
                );

            case 'Email Engagement':
                return (
                    <div className="list">
                        <div className="checkbox-list">
                            <Form.Item squareCheck noMargin type="checkbox" rounded name="checkbox6" label="Opened Email">
                                <Field />
                            </Form.Item>
                            <Form.Item squareCheck noMargin type="checkbox" rounded name="checkbox7" label="Clicked Email">
                                <Field />
                            </Form.Item>
                            <Form.Item squareCheck noMargin type="checkbox" rounded name="checkbox8" label="Replied">
                                <Field />
                            </Form.Item>
                            <Form.Item squareCheck noMargin type="checkbox" rounded name="checkbox9" label="Bounced">
                                <Field />
                            </Form.Item>
                            <Form.Item squareCheck noMargin type="checkbox" rounded name="checkbox10" label="Unsubscribed">
                                <Field />
                            </Form.Item>
                        </div>
                    </div>
                );

            case 'Company Size':
                return (
                    <div className="list">
                        <div className="checkbox-list">
                            <Form.Item squareCheck noMargin type="checkbox" rounded name="checkbox11" label="1-10 employees">
                                <Field />
                            </Form.Item>
                            <Form.Item squareCheck noMargin type="checkbox" rounded name="checkbox12" label="11-50 employees">
                                <Field />
                            </Form.Item>
                            <Form.Item squareCheck noMargin type="checkbox" rounded name="checkbox13" label="51-200 employees">
                                <Field />
                            </Form.Item>
                            <Form.Item squareCheck noMargin type="checkbox" rounded name="checkbox14" label="201-1000 employees">
                                <Field />
                            </Form.Item>
                            <Form.Item squareCheck noMargin type="checkbox" rounded name="checkbox15" label="1000 + employees">
                                <Field />
                            </Form.Item>
                        </div>
                    </div>
                );

            case 'Seniority': {
                const seniorityOptions = [
                    { value: 'entry', label: 'Entry Level' },
                    // { value: 'mid', label: 'Mid Level' },
                    { value: 'senior', label: 'Senior Level' },
                    { value: 'head', label: 'Head' },
                    { value: 'manager', label: 'Manager' },
                    { value: 'director', label: 'Director' },
                    // { value: 'vp', label: 'VP' },
                    { value: 'c_suite', label: 'C-Suite' },
                ];

                return (
                    <div className="list">
                        <Form.Item
                            colorBg
                            selectSm
                            noMargin
                            label=""
                            type="select"
                            rounded
                            isClearable
                            name="senioritySelect"
                            placeholder="Select Seniority"
                            options={seniorityOptions}
                            value={
                                searchQuery.person_seniority
                                    ? seniorityOptions.find(opt => opt.value === searchQuery.person_seniority) || { label: searchQuery.person_seniority, value: searchQuery.person_seniority }
                                    : null
                            }
                            onChange={e => {
                                const value = e?.target?.value?.value || e?.target?.value || e?.value || e;
                                const finalValue = (value && typeof value === 'object' && 'target' in value) ? '' : (value?.value || value);
                                setSearchQuery(prev => ({
                                    ...prev,
                                    person_seniority: finalValue || '',
                                    page: 1,
                                    lastSeenId: null,
                                }));
                            }}>
                            <Field />
                        </Form.Item>
                    </div>
                );
            }

            case 'Email Status':
                return (
                    <div className="list">
                        <Form.Item
                            colorBg
                            selectSm
                            noMargin
                            label=""
                            type="select"
                            rounded
                            isClearable
                            name="emailStatusSelect"
                            placeholder="Select Email Status"
                            options={emailStatusOptions}
                            value={
                                searchQuery.email_status
                                    ? emailStatusOptions.find(opt => opt.value === searchQuery.email_status) || { label: searchQuery.email_status, value: searchQuery.email_status }
                                    : null
                            }
                            onChange={e => {
                                const value = e?.target?.value?.value || e?.target?.value || e?.value || e;
                                const finalValue = (value && typeof value === 'object' && 'target' in value) ? '' : (value?.value || value);
                                setSearchQuery(prev => ({
                                    ...prev,
                                    email_status: finalValue || '',
                                    page: 1,
                                    lastSeenId: null,
                                }));
                            }}>
                            <Field />
                        </Form.Item>
                    </div>
                );

            case 'Job Start Date':
                return (
                    <div className="list">
                        <Form.Item
                            colorBg
                            inputSm
                            noMargin
                            label="Select Date Range"
                            type="datepicker"
                            selectsRange={true}
                            name="jobStartDateRange"
                            autoComplete="one-time-code"
                            clear={!!searchQuery.job_start_date && (searchQuery.job_start_date[0] || searchQuery.job_start_date[1])}
                            value={searchQuery.job_start_date}
                            onChange={e => {
                                const value = e?.target?.value || [null, null];

                                setSearchQuery(prev => ({
                                    ...prev,
                                    job_start_date: (value[0] || value[1]) ? value : '',
                                    page: 1,
                                    lastSeenId: null,
                                }));
                            }}>
                            <Field />
                        </Form.Item>
                    </div>
                );

            default:
                if (textFiltersMap[filter]) {
                    const queryKey = textFiltersMap[filter];
                    return (
                        <div className="list">
                            <Form.Item
                                colorBg
                                inputSm
                                noMargin
                                label=""
                                type="text"
                                name={queryKey}
                                placeholder={`Enter ${filter}`}
                                value={getTextDraft(queryKey)}
                                autoComplete="one-time-code"
                                onChange={e => handleTextFilterChange(queryKey, e?.target?.value || '')}>
                                <Field />
                            </Form.Item>
                        </div>
                    );
                }
                return (
                    <ul className="list">
                        <li>
                            <span className="text">No options</span>
                        </li>
                    </ul>
                );
        }
    };

    const filtersList = customFilters || [
        'Lead Status',
        // 'Lead Owner',
        'Contacted Status',
        'Lead Source',
        'Campaign',
        'Tags',
        'List',
        'Date Created',
        // 'Last Activity',
        // 'Email Engagement',
        'Company Name',
        'Job Title',
        // 'Industry',
        // 'Company Size',
        // 'Location',
    ].filter(filter => !(hideListFilter && filter === 'List'));

    return (
        <StyledFiltersList>
            {filtersList.map(filter => (
                <li key={filter} className={openFilters.includes(filter) ? 'active' : ''}>
                    <button className="opener" type="button" onClick={() => toggleFilter(filter)}>
                        {filter}
                        <IoIosArrowDown className="icon" />
                    </button>

                    <div
                        className="slide"
                        style={{
                            maxHeight: openFilters.includes(filter) ? '500px' : '0',
                            overflow: 'hidden',
                            transition: 'max-height 0.35s ease, opacity 0.3s ease',
                            opacity: openFilters.includes(filter) ? 1 : 0,
                        }}>
                        {renderFilterContent(filter)}
                    </div>
                </li>
            ))}
        </StyledFiltersList>
    );
};

export default Filters;